import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FooterSection from '../../components/sections/FooterSection';

/*
    We need a visa application tracking page in this component. 
    Applications are CRUD-accessed by ID. Specific changes to a specific
    application's datafields in MongoDB are RBAC/ABAC access based, but all
    admins, managers and the author of the application has to view all aspects of it. 

    We will use the localStorage to see what user is logged in, roles, attributes, etc.

    I need UIKit Accordeons (dropdown-alike thingies that expand and hide stuff)
    for each stage of immigration. Inside each accordion we'll need UIKit's icon (done not done, good not good - get creative)
    - Upload Documents
    - Application Created
    - Application Sent to Immigration Services
    - Response Received
    - Visa Approved
    - Job Found
    - Arrived in Kazakhstan 
    - Accomodation is Ready

    Inside each accordion, when its' opened, we need a To-Do like table (list), and each
    element-item in the list needs a button "Details" (uk-button-jai). Clicking it leads to a redirect
    to dedicated subpage of online platforms. Pages are NOT ready yet, so just make the buttons lead to some NA page

    From top to bottom, each accordion is not interactive (grayed-out/disabled) until the current stage is fulfilled.
    
    Fulfillment will be manually checked by Immigration Specialist responsible for each specific user/client.

    The beginning of the uk-container "umbrella" should have hardcoded breadcrumb: [Home > Applications > Tracking]
    
    At last, add this page to App.js with route "/platform/applications/:id" (slug? slum? idk name, but you got the idea), since
    I need ability to live-preview this page-draft in web browser - that's the reason.
*/

/**
 * Represents a page for tracking the status of a visa application.
 * This component displays the application progress through a series of accordion panels,
 * each representing a stage in the immigration process.
 * @param {object} props - The props for the component.
 * @returns {JSX.Element} The rendered ApplicationTrackingPage component.
 */
function ApplicationTrackingPage(props) {
    var id = useParams().id;
    var t = useTranslation().t;

    /**
     * @typedef {object} Task
     * @property {string} name - The name of the task.
     * @property {string} detailsUrl - The URL for the task's details page.
     */

    /**
     * @typedef {object} Stage
     * @property {string} title - The title of the stage.
     * @property {boolean} isCompleted - Whether the stage is completed.
     * @property {boolean} isActive - Whether the stage is the current active one.
     * @property {Task[]} tasks - A list of tasks within the stage.
     */

    /**
     * Mock data for the application stages.
     * In a real application, this would be fetched from a backend based on the application `id`.
     * The `currentStageIndex` simulates the progress which would be controlled by an admin/manager.
     * @type {Stage[]}
     */
    var initialStages = [
        { title: "Upload Documents", tasks: [{ name: "Fill out Passport Data", detailsUrl: "#", description: "You need to fill out your passport information exactly as it appears in your document." }, { name: "Scan of Education Diploma", detailsUrl: "#", description: "Upload a high-quality color scan of your highest education diploma or certificate." }] },
        { title: "Application Created", tasks: [{ name: "Review Application Form", detailsUrl: "#", description: "Carefully check all the data you have provided in the application form before it is submitted." }] },
        { title: "Application Sent to Immigration Services", tasks: [{ name: "Track Submission", detailsUrl: "#", description: "Your application has been sent. You can monitor the official status using the provided tracking number." }] },
        { title: "Response Received", tasks: [{ name: "View Official Response", detailsUrl: "#", description: "An official response from the immigration services has been received. Click details to view it." }] },
        { title: "Visa Approved", tasks: [{ name: "Download Visa", detailsUrl: "#", description: "Congratulations! Your visa has been approved. You can download the electronic version here." }] },
        { title: "Job Found", tasks: [{ name: "Review Job Offer", detailsUrl: "#", description: "A job offer has been secured for you. Please review the terms and conditions." }] },
        { title: "Arrived in Kazakhstan", tasks: [{ name: "Confirm Arrival", detailsUrl: "#", description: "Please confirm your arrival in Kazakhstan so we can proceed with the next steps of your registration." }] },
        { title: "Accomodation is Ready", tasks: [{ name: "View Accomodation Details", detailsUrl: "#", description: "Your accommodation has been arranged. Click for details about the location and check-in process." }] },
    ];

    // This state simulates which stage is currently active. For this draft, it's hardcoded.
    var currentStageState = useState(0);
    var currentStageIndex = currentStageState[0];
    var setCurrentStageIndex = currentStageState[1];

    /**
     * Processes the initial stages to set their `isCompleted` and `isActive` states.
     * @type {Stage[]}
     */
    // This loop processes the raw stage data (`initialStages`) and enriches it with
    // status information (`isCompleted`, `isActive`) based on the application's
    // current progress (`currentStageIndex`).
    // The result is a new array `stages` which is used for rendering.
    // This approach avoids direct mutation of the source data.
    var stages = [];

    for (var i = 0; i < initialStages.length; i++) {
        // Create a new object for the stage to avoid modifying the original `initialStages` array.
        var originalStage = initialStages[i];
        var processedStage = {
            title: originalStage.title,
            tasks: originalStage.tasks,
            isCompleted: false, // Default value
            isActive: false     // Default value
        };

        // Explicitly determine if the stage is completed.
        // A stage is considered completed if its index is less than the current active stage's index.
        if (i < currentStageIndex) {
            processedStage.isCompleted = true;
        } else {
            processedStage.isCompleted = false;
        }

        // Explicitly determine if the stage is the currently active one.
        // A stage is considered active if its index matches the current active stage's index.
        if (i === currentStageIndex) {
            processedStage.isActive = true;
        } else {
            processedStage.isActive = false;
        }

        stages.push(processedStage);
    }

    return (
        <>
            <div className="uk-container uk-container-xsmall uk-margin-top uk-margin-large-bottom">
                {/* Hardcoded breadcrumb as requested */}
                <ul className="uk-breadcrumb">
                    <li><Link to="/">Home</Link></li>
                    <li><Link to="/my-account">Applications</Link></li>
                    <li><span>Tracking</span></li>
                </ul>

                <h1 className="uk-heading-medium uk-margin-top">Application Status</h1>

                <ul data-uk-accordion>
                    {stages.map((stage, index) => {
                        var isDisabled = index > currentStageIndex;
                        var icon;
                        var iconColor;
                        var liClassName;

                        if (stage.isCompleted) {
                            icon = "check-circle";
                            iconColor = "uk-text-success";
                        } else if (stage.isActive) {
                            icon = "play-circle";
                            iconColor = "uk-text-primary";
                        } else {
                            icon = "eye-slash";
                            iconColor = "uk-text-muted";
                        }

                        if (isDisabled) {
                            liClassName = "uk-disabled";
                        } else if (stage.isActive) {
                            liClassName = "uk-open";
                        } else {
                            liClassName = "";
                        }

                        return (
                            <li 
                                key={index} 
                                className={liClassName}
                            >
                                <a className="uk-accordion-title" href="#">
                                    <span data-uk-icon={`icon: ${icon}`} className={`uk-margin-small-right ${iconColor}`}></span>
                                    <a className={`${iconColor}`}>{stage.title}</a> 
                                </a>
                                <div className="uk-accordion-content">
                                    <table className="uk-table uk-table-divider uk-table-middle">
                                        <thead>
                                            <tr>
                                                <th className="uk-width-expand">TASKS</th>
                                                <th className="uk-width-small">COMPLETION</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stage.tasks.map((task, taskIndex) => (
                                                <tr key={taskIndex}>
                                                    <td data-uk-tooltip={`title: ${task.description}; pos: left`}>
                                                        {task.name}
                                                    </td>
                                                    <td>
                                                        <Link to={task.detailsUrl} className="uk-button uk-button-jai uk-button-small">Details</Link>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
            <FooterSection />
        </>
    );
}

export default ApplicationTrackingPage;